/**
 * Quantity Limit Manager — LF PRO
 *
 * Limite de quantidade por variante, por pedido (camada de TEMA / UX), com REGRAS:
 *   - Máximo Padrão: vale pra loja toda (o que não estiver numa regra).
 *   - Regras específicas: cada uma tem produtos + coleções + seu próprio máximo.
 *   - Precedência por produto: regra-de-produto > regra-de-coleção > padrão.
 *
 * DUAS camadas de defesa no tema:
 *   1. PREVENTIVA (síncrona): capa o valor no instante em que o cliente digita/clica,
 *      antes de qualquer AJAX/submit (seletor da PDP, inputs do carrinho, botão de checkout).
 *   2. REATIVA (rede): escuta cart-update, re-lê /cart.js e clampa o excedente.
 *
 * Coleções resolvidas no cliente via /collections/{handle}/products.json (paginado, cacheado)
 * porque /cart.js não diz a que coleções um produto pertence.
 *
 * LIMITE HONESTO: roda no navegador. NÃO segura console/permalink/API/checkout express que
 * pule o form. Trava 100% server-side = Cart & Checkout Validation Function (Fase 2).
 *
 * @version 2.0.0
 * @author EcoUp / LFpro
 */

class QtyLimitManager extends HTMLElement {
  constructor() {
    super();
    this.config = window.qtyLimitConfig || { enabled: false };

    const dm = parseInt(this.config.defaultMax, 10);
    this.defaultMax = (Number.isFinite(dm) && dm >= 1) ? dm : Infinity;

    this.rules = (this.config.rules || []).map((r) => ({
      enabled: !!r.enabled,
      productIds: (r.productIds || []).map(Number),
      collectionHandles: r.collectionHandles || [],
      max: parseInt(r.max, 10),
      collectionProductIds: new Set(),
    }));

    this.currentCart = null;
    this.cartByKey = {};
    this.isProcessing = false;
    this.cartUpdateTimeout = null;
    this._lastNotify = 0;
  }

  connectedCallback() {
    if (!this.config.enabled) return;
    if (document.readyState === 'complete') {
      setTimeout(() => this.init(), 200);
    } else {
      window.addEventListener('load', () => setTimeout(() => this.init(), 200));
    }
  }

  async init() {
    this.installPreventiveGuards(); // camada 1 (síncrona)
    this.attachCartListener();       // camada 2 (reativa)
    await this.resolveCollections(); // resolve coleções -> ids de produto
    this.handleCartUpdate();         // primeira varredura (já com coleções)
    this.capPdpInputs();
  }

  // ==========================================================================
  // Regras / escopo
  // ==========================================================================

  isGift(item) {
    return !!(item.properties && item.properties._is_gift === 'true');
  }

  /** Máximo efetivo pra um produto. Precedência: regra-produto > regra-coleção > padrão. Infinity = sem limite. */
  maxForProduct(productId) {
    const pid = Number(productId);
    const prodMaxes = [];
    const colMaxes = [];
    for (const r of this.rules) {
      if (!r.enabled || !Number.isFinite(r.max) || r.max < 1) continue;
      if (r.productIds.includes(pid)) prodMaxes.push(r.max);
      else if (r.collectionProductIds.has(pid)) colMaxes.push(r.max);
    }
    if (prodMaxes.length) return Math.min(...prodMaxes);
    if (colMaxes.length) return Math.min(...colMaxes);
    return this.defaultMax;
  }

  async resolveCollections() {
    const handles = new Set();
    this.rules.forEach((r) => {
      if (r.enabled) (r.collectionHandles || []).forEach((h) => h && handles.add(h));
    });
    if (!handles.size) return;

    const map = {};
    await Promise.all(
      [...handles].map(async (h) => {
        map[h] = await this.fetchCollectionProductIds(h);
      })
    );
    this.rules.forEach((r) => {
      r.collectionProductIds = new Set();
      (r.collectionHandles || []).forEach((h) => (map[h] || []).forEach((pid) => r.collectionProductIds.add(pid)));
    });
  }

  async fetchCollectionProductIds(handle) {
    const ids = [];
    try {
      for (let page = 1; page <= 20; page++) {
        const res = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        const prods = data.products || [];
        prods.forEach((p) => ids.push(Number(p.id)));
        if (prods.length < 250) break;
      }
    } catch (e) {}
    return ids;
  }

  msgText(m) {
    return String(this.config.message || 'Máximo de {max} unidades por item.').replace(/\{max\}/g, m);
  }

  notify(m) {
    const now = Date.now();
    if (now - this._lastNotify < 1500) return;
    this._lastNotify = now;
    try {
      if (typeof window.showWarning === 'function') window.showWarning(this.msgText(m), 3000);
    } catch (e) {}
  }

  /** Qtd não-brinde da mesma variante em OUTRAS linhas (exclui a linha `key`). */
  otherVariantQty(item, key) {
    let sum = 0;
    (this.currentCart?.items || []).forEach((it) => {
      if (it.key === key) return;
      if (this.isGift(it)) return;
      if (String(it.variant_id) === String(item.variant_id)) sum += it.quantity;
    });
    return sum;
  }

  // ==========================================================================
  // Camada 1 — PREVENTIVA (síncrona)
  // ==========================================================================

  installPreventiveGuards() {
    if (window.qtyLimitGuardsInstalled) return;
    const self = () => document.querySelector('qty-limit-manager') || this;
    const clampHandler = (e) => self().onQtyInputCapture(e);
    document.addEventListener('input', clampHandler, true);
    document.addEventListener('change', clampHandler, true);
    document.addEventListener('click', (e) => self().onCheckoutClickCapture(e), true);
    window.qtyLimitGuardsInstalled = true;
  }

  onQtyInputCapture(e) {
    const t = e.target;
    if (!t || t.tagName !== 'INPUT') return;

    // input de linha do carrinho / side cart
    if (t.matches('[data-cart-quantity]')) {
      const item = this.cartByKey[t.dataset.line];
      if (!item || this.isGift(item)) return;
      const m = this.maxForProduct(item.product_id);
      if (!Number.isFinite(m)) return;
      const allowed = Math.max(0, m - this.otherVariantQty(item, t.dataset.line));
      const v = parseInt(t.value, 10);
      if (!isNaN(v) && v > allowed) {
        t.value = allowed;
        this.notify(m);
      }
      return;
    }

    // seletor de quantidade da página de produto (pega o "Comprar agora")
    if (t.matches('input[name="quantity"]') && t.closest('quantity-input') && t.dataset.product) {
      const m = this.maxForProduct(t.dataset.product);
      if (!Number.isFinite(m)) return;
      const v = parseInt(t.value, 10);
      if (!isNaN(v) && v > m) {
        t.value = m;
        this.notify(m);
      }
    }
  }

  onCheckoutClickCapture(e) {
    const btn = e.target.closest && e.target.closest('#cart-checkout, #cart-sidebar-checkout');
    if (!btn) return;
    const overflows = this.computeOverflows(this.currentCart || { items: [] });
    if (!overflows.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const url = this.checkoutUrl(btn);
    this.handleCartUpdate().then(() => {
      if (url) window.location.href = url;
    });
  }

  checkoutUrl(btn) {
    if (btn.dataset && btn.dataset.href) return btn.dataset.href;
    const oc = btn.getAttribute && btn.getAttribute('onclick');
    if (oc) {
      const m = oc.match(/'([^']*\/checkout[^']*)'/);
      if (m) return m[1];
    }
    const root = (window.routes && window.routes.root) ? String(window.routes.root).replace(/\/$/, '') : '';
    return root + '/checkout';
  }

  capPdpInputs() {
    document.querySelectorAll('quantity-input input[name="quantity"][data-product]').forEach((inp) => {
      const m = this.maxForProduct(inp.dataset.product);
      if (Number.isFinite(m)) inp.max = m;
      else inp.removeAttribute('max');
    });
  }

  // ==========================================================================
  // Camada 2 — REATIVA
  // ==========================================================================

  attachCartListener() {
    if (window.qtyLimitListenersAttached) return;
    document.addEventListener('cart-update', (e) => {
      if (e.detail && e.detail.source === 'qty-limit') return;
      const mgr = document.querySelector('qty-limit-manager');
      if (!mgr) return;
      if (document.body.classList.contains('cart-sidebar-show')) mgr.handleCartUpdate();
      else mgr.debouncedCartUpdate();
    });
    window.qtyLimitListenersAttached = true;
  }

  debouncedCartUpdate() {
    if (this.cartUpdateTimeout) clearTimeout(this.cartUpdateTimeout);
    const delay = document.body.classList.contains('cart-sidebar-show') ? 300 : 1000;
    this.cartUpdateTimeout = setTimeout(() => this.handleCartUpdate(), delay);
  }

  async fetchCart() {
    const res = await fetch('/cart.js');
    this.currentCart = await res.json();
    this.cartByKey = {};
    (this.currentCart.items || []).forEach((it) => { this.cartByKey[it.key] = it; });
    return this.currentCart;
  }

  computeOverflows(cart) {
    const byVariant = {};
    (cart.items || []).forEach((item) => {
      if (this.isGift(item)) return;
      const k = item.variant_id;
      if (!byVariant[k]) byVariant[k] = { total: 0, productId: item.product_id, lines: [] };
      byVariant[k].total += item.quantity;
      byVariant[k].lines.push(item);
    });
    const overflows = [];
    Object.keys(byVariant).forEach((vid) => {
      const g = byVariant[vid];
      const m = this.maxForProduct(g.productId);
      if (Number.isFinite(m) && g.total > m) {
        overflows.push({ variantId: vid, total: g.total, max: m, lines: g.lines });
      }
    });
    return overflows;
  }

  buildChanges(overflows) {
    const changes = [];
    overflows.forEach((ov) => {
      let excess = ov.total - ov.max;
      for (let i = ov.lines.length - 1; i >= 0 && excess > 0; i--) {
        const line = ov.lines[i];
        const reduceBy = Math.min(line.quantity, excess);
        changes.push({ key: line.key, quantity: line.quantity - reduceBy });
        excess -= reduceBy;
      }
    });
    return changes;
  }

  async clamp(changes) {
    for (const ch of changes) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: ch.key, quantity: ch.quantity }),
      });
    }
  }

  async refreshSurface() {
    const path = window.location.pathname;
    if (path === '/cart' || path.endsWith('/cart')) {
      window.location.reload();
      return true;
    }
    if (
      document.body.classList.contains('cart-sidebar-show') &&
      typeof $ !== 'undefined' &&
      window.routes &&
      window.routes.root !== undefined
    ) {
      await new Promise((resolve) => {
        $.ajax({
          type: 'GET',
          url: window.routes.root + '/cart?view=ajax_side_cart',
          cache: false,
          success: (data) => {
            $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper').html($(data));
            resolve();
          },
          error: () => resolve(),
        });
      });
    }
    return false;
  }

  async handleCartUpdate() {
    if (this.isProcessing) {
      const delay = document.body.classList.contains('cart-sidebar-show') ? 300 : 1000;
      setTimeout(() => this.handleCartUpdate(), delay);
      return;
    }
    this.isProcessing = true;
    try {
      await this.fetchCart();
      const overflows = this.computeOverflows(this.currentCart);

      if (!overflows.length) {
        this.capInputs(this.currentCart);
        this.capPdpInputs();
        return;
      }

      await this.clamp(this.buildChanges(overflows));
      const reloaded = await this.refreshSurface();
      if (reloaded) return;

      await this.fetchCart();
      this.capInputs(this.currentCart);
      this.capPdpInputs();
      document.dispatchEvent(
        new CustomEvent('cart-update', { detail: { source: 'qty-limit', cart: this.currentCart } })
      );
    } catch (error) {
      // silencioso
    } finally {
      this.isProcessing = false;
    }
  }

  capInputs(cart) {
    if (!cart) return;
    const totals = {};
    (cart.items || []).forEach((it) => {
      if (this.isGift(it)) return;
      totals[it.variant_id] = (totals[it.variant_id] || 0) + it.quantity;
    });

    document.querySelectorAll('cart-update-quantity').forEach((container) => {
      const input = container.querySelector('[data-cart-quantity]');
      if (!input) return;
      const item = this.cartByKey[input.dataset.line];
      if (!item || this.isGift(item)) return;

      const m = this.maxForProduct(item.product_id);
      if (!Number.isFinite(m)) {
        this.toggleMessage(container, false);
        return;
      }
      input.max = m;
      const atLimit = (totals[item.variant_id] || 0) >= m;
      const plus = container.querySelector('.plus');
      if (plus) {
        plus.style.pointerEvents = atLimit ? 'none' : '';
        plus.style.opacity = atLimit ? '0.4' : '';
        plus.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      }
      this.toggleMessage(container, atLimit, this.msgText(m));
    });
  }

  toggleMessage(container, show, msg) {
    const host = container.parentNode;
    if (!host) return;
    let el = host.querySelector(':scope > .qty-limit-msg');
    if (show) {
      if (!el) {
        el = document.createElement('div');
        el.className = 'qty-limit-msg';
        el.style.cssText = 'font-size:11px;color:#c0392b;margin-top:4px;line-height:1.3;';
        host.insertBefore(el, container.nextSibling);
      }
      el.textContent = msg;
    } else if (el) {
      el.remove();
    }
  }
}

if (!customElements.get('qty-limit-manager')) {
  customElements.define('qty-limit-manager', QtyLimitManager);
}
